import { type ReactNode, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, Plus, Upload } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import { useToast } from '@/stores/toast'
import { Modal } from './Modal'
import { SearchBox } from './SearchBox'
import { PartnerPicker } from './PartnerPicker'
import { DateRangeBar } from './DateRangeBar'
import { cn, formatNum, todayISO } from '@/lib/utils'
import { useCrudMutations } from '@/lib/useCrudMutations'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'
import { useFileDownload } from '@/lib/useFileDownload'
import { useAuth } from '@/stores/auth'
import { currentCompanyId } from '@/lib/companyUrl'

const TX_FORM_FIELDS = ['date', 'partner', 'type', 'amount', 'note'] as const

/** 수금(Collections) / 지급(Payments) 공통 골격.
 *  필드 접두어(`receipt_*` vs `payment_*`) 만 config로 주입해 렌더.
 */

// 공통 raw record (필드 이름은 config로 지정)
type TxRecord = {
  id: number
  partner: number
  partner_code: string
  partner_name: string
  amount: string | number
  bank_account: number | null
  note: string
  // 아래는 config.dateField/noField/typeField/typeLabelField 로 접근
  [key: string]: unknown
}

interface TxForm {
  id?: number
  partner: number | null
  amount: number
  bank_account: number | null
  note: string
  // 접두어별 필드 — record<string, unknown>로 두고 접근 시 캐스팅
  [key: string]: unknown
}

export interface TxRegistryConfig {
  // 헤더
  icon: ReactNode
  headerTitle: string           // '수금등록' | '지급등록'

  // 라벨
  entityShort: string           // '수금' | '지급'
  entityDateLabel: string       // '수금일자'
  entityNoLabel: string         // '수금번호'
  entityTypeLabel: string       // '수금유형'
  partnerLabel: string          // '거래처' | '매입처'
  searchPlaceholder: string
  emptyText: string

  // API / query
  apiBase: string               // '/api/collections'
  queryKey: string              // 'collections'

  // 유형 옵션
  typeOptions: readonly (readonly [string, string])[]
  defaultType: string

  // 필드 매핑 (API 응답/요청 키)
  dateField: string             // 'receipt_date' | 'payment_date'
  noField: string               // 'receipt_no' | 'payment_no'
  typeField: string             // 'receipt_type' | 'payment_type'
  typeLabelField: string        // 'receipt_type_label' | 'payment_type_label'

  // 파트너
  partnerBizClass?: 'customer' | 'vendor' | 'both' | ''

  // 색상 팔레트
  accent: {
    button: string              // 'bg-emerald-600 hover:bg-emerald-700'
    total: string               // 'text-emerald-700'
    typeChip: string            // 'bg-emerald-100 text-emerald-800'
  }
}

export function TxRegistryTab({ config: c }: { config: TxRegistryConfig }) {
  const push = useToast((s) => s.push)
  const [dateFrom, setDateFrom] = useState('')   // 조회 기본 빈 날짜(직접 지정)
  const [dateTo, setDateTo] = useState('')
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<TxForm | null>(null)

  const blankForm = (): TxForm => ({
    [c.dateField]: todayISO(),
    partner: null,
    [c.typeField]: c.defaultType,
    amount: 0,
    bank_account: null,
    note: '',
  })

  const { data: rows = [] } = useQuery({
    queryKey: [c.queryKey, dateFrom, dateTo, search],
    queryFn: () => {
      const p = new URLSearchParams({ from: dateFrom, to: dateTo })
      if (search) p.set('q', search)
      return api<TxRecord[]>(`${c.apiBase}?${p}`)
    },
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })

  const { save, remove } = useCrudMutations<TxForm>({
    apiBase: c.apiBase,
    queryKey: c.queryKey,
    onSaved: () => setEdit(null),
  })

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(TX_FORM_FIELDS)

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<TxRecord>({
    items: rows,
    disabled: !!edit,
    onEnter: (r) => setEdit({
      id: r.id, partner: r.partner,
      [c.dateField]: r[c.dateField],
      [c.typeField]: r[c.typeField],
      amount: Number(r.amount),
      bank_account: r.bank_account, note: r.note,
    }),
    onDelete: (r) => {
      void confirm(`${String(r[c.noField] ?? '')} ${c.entityShort}을 삭제할까요?`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0)

  // 엑셀 다운로드 — 지금 조회한 기간·검색어 그대로, 올리기 양식 열 순서(+ erp코드)
  const { downloading, download } = useFileDownload()
  const exportExcel = () => {
    const p = new URLSearchParams({ from: dateFrom, to: dateTo })
    if (search) p.set('q', search)
    void download(`${c.apiBase}/export?${p}`, `${c.entityShort}내역_${dateFrom}_${dateTo}.xlsx`)
  }

  // ── 엑셀 일괄 업로드(날짜·erp코드·거래처명·유형·금액·적요). 오류가 하나라도 있으면 전부 저장 안 함.
  //    이미 같은 날짜·거래처·유형·금액이 있으면 409 → 확인 후 allow_dup=1 로 다시 보냄.
  const qc = useQueryClient()
  const excelInputRef = useRef<HTMLInputElement>(null)
  type ImportOk = { created_count: number; total: string; date_from: string; date_to: string; skipped?: string[] }
  const importExcel = useMutation({
    mutationFn: async (file: File): Promise<ImportOk | null> => {
      const send = async (allowDup: boolean) => {
        const fd = new FormData()
        fd.append('file', file)
        const token = useAuth.getState().token
        const cid = currentCompanyId()
        const res = await fetch(`${c.apiBase}/import-excel${allowDup ? '?allow_dup=1' : ''}`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(cid ? { 'X-Company-Id': String(cid) } : {}),
          },
          body: fd,
        })
        return { res, data: await res.json().catch(() => ({})) }
      }
      let { res, data } = await send(false)
      if (res.status === 409) {
        const list = (data.duplicates ?? []).slice(0, 10).join('\n')
        const ok = await confirm(`${data.detail}\n${list}${(data.duplicates?.length ?? 0) > 10 ? '\n…' : ''}\n\n그래도 모두 등록할까요?`)
        if (!ok) return null
        ;({ res, data } = await send(true))
      }
      if (!res.ok) {
        const errs = Array.isArray(data.errors) ? data.errors.slice(0, 15).join('\n') : ''
        throw new Error([data.detail, errs].filter(Boolean).join('\n') || `HTTP ${res.status}`)
      }
      return data as ImportOk
    },
    onSuccess: (data) => {
      if (!data) return
      qc.invalidateQueries({ queryKey: [c.queryKey] })
      setDateFrom(data.date_from)        // 올린 기간을 바로 보여줌
      setDateTo(data.date_to)
      const sk = data.skipped ?? []
      push(`엑셀 등록 완료 — ${c.entityShort} ${data.created_count}건, ${formatNum(Number(data.total))}원`
        + (sk.length ? `\n금액 없음 ${sk.length}줄 건너뜀:\n${sk.slice(0, 10).join('\n')}` : ''),
        'success', sk.length ? 15000 : 6000)
    },
    onError: (e) => push(`엑셀 등록 실패\n${e instanceof Error ? e.message : String(e)}`, 'error', 12000),
  })

  return (
    <div className="max-w-7xl mx-auto p-6">
      <header className="mb-5 flex items-center gap-4">
        {c.icon}
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex-1">{c.headerTitle}</h2>
        <input ref={excelInputRef} type="file" accept=".xlsx" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importExcel.mutate(f) }}/>
        <button onClick={() => excelInputRef.current?.click()} disabled={importExcel.isPending}
                title={`엑셀 양식: 날짜 | erp코드 | 거래처명 | ${c.entityTypeLabel} | 금액 | 적요 — 유형은 ERP 유형 이름 그대로`}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          {importExcel.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
          엑셀로 추가
        </button>
        <button onClick={exportExcel} disabled={downloading || !rows.length}
                title="지금 조회한 기간·검색 결과를 엑셀로(erp코드 포함, 올리기 양식과 같은 열)"
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
        <button onClick={() => setEdit(blankForm())}
                data-role="add-btn"
                title="Alt+N"
                className={cn('text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5', c.accent.button)}>
          <Plus className="w-4 h-4" /> {c.entityShort} 추가
        </button>
      </header>

      <DateRangeBar from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}>
        <SearchBox value={search} onChange={setSearch}
                   placeholder={c.searchPlaceholder} className="flex-1 min-w-[200px]" />
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
      </DateRangeBar>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-28">{c.entityDateLabel}</th>
              <th className="px-3 py-2.5 text-left font-medium w-44">{c.entityNoLabel}</th>
              {/* 테이블 컬럼은 '거래처' 로 통일 (원본 UX). 모달/폼 라벨은 c.partnerLabel(매입처 등)로 구분. */}
              <th className="px-3 py-2.5 text-left font-medium w-20">코드</th>
              <th className="px-3 py-2.5 text-left font-medium">거래처</th>
              <th className="px-3 py-2.5 text-left font-medium w-28">{c.entityTypeLabel}</th>
              <th className="px-3 py-2.5 text-right font-medium w-32">금액</th>
              <th className="px-3 py-2.5 text-left font-medium">적요</th>
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {rows.map((r, idx) => (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                  className="border-t border-slate-100 hover:bg-slate-50 focus:bg-blue-50 focus:outline-none">
                <td className="px-3 py-2 tabular-nums text-slate-600">{String(r[c.dateField] ?? '')}</td>
                <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-700">{String(r[c.noField] ?? '')}</td>
                <td className="px-3 py-2 font-mono text-xs tabular-nums text-slate-500">{r.partner_code}</td>
                <td className="px-3 py-2 text-slate-900 font-medium">{r.partner_name}</td>
                <td className="px-3 py-2">
                  <span className={cn('text-xs px-2 py-0.5 rounded-full font-semibold', c.accent.typeChip)}>
                    {String(r[c.typeLabelField] ?? '')}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatNum(r.amount)}</td>
                <td className="px-3 py-2 text-slate-500 text-xs truncate max-w-[200px]">{r.note}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEdit({
                    id: r.id, partner: r.partner,
                    [c.dateField]: r[c.dateField],
                    [c.typeField]: r[c.typeField],
                    amount: Number(r.amount),
                    bank_account: r.bank_account, note: r.note,
                  })} className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                  <button onClick={() => { void confirm(`${String(r[c.noField] ?? '')} ${c.entityShort}을 삭제할까요?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700 text-xs">삭제</button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-10 text-sm">{c.emptyText}</td></tr>
            )}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td colSpan={5} className="px-3 py-2 text-right text-xs text-slate-600">합 계</td>
              <td className={cn('px-3 py-2 text-right tabular-nums', c.accent.total)}>{formatNum(total)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? `${c.entityShort} 수정` : `${c.entityShort} 추가`}>
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!edit.partner) { push(`${c.partnerLabel}를 선택하세요`, 'warn'); return }
                  save.mutate(edit)
                }} className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{c.entityDateLabel} *</label>
              <input data-form-field="date" autoFocus type="date" value={String(edit[c.dateField] ?? '')}
                     onChange={(e) => setEdit({ ...edit, [c.dateField]: e.target.value })} required
                     className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm tabular-nums"/>
            </div>
            <div data-form-field="partner">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{c.partnerLabel} *</label>
              <PartnerPicker value={edit.partner} onChange={(p) => setEdit({ ...edit, partner: p.id })}
                             bizClass={c.partnerBizClass ?? ''} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{c.entityTypeLabel} *</label>
                <select data-form-field="type" value={String(edit[c.typeField] ?? '')}
                        onChange={(e) => setEdit({ ...edit, [c.typeField]: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                  {c.typeOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">금액 *</label>
                <input data-form-field="amount" type="number" step="0.01" value={edit.amount}
                       onChange={(e) => setEdit({ ...edit, amount: Number(e.target.value) })} required
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-right tabular-nums"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">적요</label>
              <input data-form-field="note" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })}
                     className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit"
                      className={cn('px-4 py-2 text-sm text-white font-semibold rounded-lg', c.accent.button)}>
                저장
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
